import React, { useEffect, useMemo, useState } from "react";
import { Button, Form, FormInstance, Radio, Spin } from "@pionex-web-kit/components";
import { BuyCryptoConfig, BuyCryptoParams } from "landings/V2/BuyCrypto/types";
import { PaymentMethod, usePaymentMethods } from "landings/V2/BuyCrypto/Buy/cards";
import { useTranslators } from "commonUse/Locale";
import { useDefaultPaymentMethod, usePreviewAmount, useShortcuts } from "landings/V2/BuyCrypto/hooks";
import { ACH_AccountItem } from "landings/V2/CircleACH/utils";
import { BUY_CRYPTO_ROUTE } from "landings/V2/BuyCrypto/menus";
import TReportLink from "components/v2/TinyComps";
import csn from "classnames";
import cs from "./index.m.less";
import { IconHistory24px } from "@pionex-web-kit/icons";
import KycLoginClick from "landings/V2/BuyCrypto/components/KycLoginClick";
import DataReporter from "landings/Analytics/DataReporter";
import { EventName } from "landings/Analytics/EventName";
import NumberUtils from "utils/NumberUtils";
import PayMethodItem from "landings/V2/BuyCrypto/components/Form/items/PayMethodItem";
import SelectCoinItem from "landings/V2/BuyCrypto/components/Form/items/SelectCoinItem";
import AmountItem from "landings/V2/BuyCrypto/components/Form/items/AmountItem";
import { SafeDecimal } from "trade_utils_lib";
import useMedia from "src/components-v2/Layout/hooks/useMedia";
import useAccountInfo from "commonUse/useAccountInfo";
import { useLocation } from "react-router";
import { useBalanceTradeByBaseQuote } from "commonUse/useBalance";
import StripeACH from "../../StripeACH";

export type PreviewAmountInfo = Pick<ReturnType<typeof usePreviewAmount>, "previewAmount" | "previewAmountPrecision">;

interface Props {
    className?: string;
    config?: BuyCryptoConfig;
    onFinish?: (paymentMethod: PaymentMethod, params: BuyCryptoParams) => void;
    form?: FormInstance;
    onPreviewAmount?: (previewAmountInfo: PreviewAmountInfo) => void;
}

export const FORM_KEY_PAYMENTMETHOD = "payMethod";
export const FORM_KEY_AMOUNT = "amount";

/**
 * 买币填写的表单
 */
const BuyCryptoForm: React.FC<Props> = ({ className, config, onFinish, form: outForm, onPreviewAmount }) => {
    const { $st } = useTranslators();
    const { isTablet } = useMedia();
    const { userId } = useAccountInfo();
    const { search } = useLocation();
    const params = new URLSearchParams(search);
    const selectedCoin = params.get("coin");
    const sortMap = useMemo(() => {
        return {
            BTC: 1,
            ETH: 2,
            USDT: 3,
            SHIB: 4,
            DOGE: 5,
            SOL: 6,
            XRP: 7,
            UNI: 8,
            ADA: 9,
            MATIC: 10,
            ETC: 11,
            XLM: 12,
            FIL: 13,
            RNDR: 14,
            ARB: 15,
            ICP: 16,
            LTC: 17,
            ATOM: 18,
            AVAX: 19,
        };
    }, []);
    const coins = useMemo(() => {
        return config?.coins?.sort((a, b) => {
            return sortMap[a.coin] - sortMap[b.coin];
        });
    }, [sortMap, config?.coins]);
    const [form] = Form.useForm(outForm);
    const { data: paymentMethods, refresh, dataLoaded } = usePaymentMethods(form);
    useEffect(() => {
        if (selectedCoin) {
            form.setFieldsValue({ coin: selectedCoin });
            return;
        }
        if (!config?.coins.length) {
            return;
        }

        form.setFieldsValue({ coin: config?.coins[0].coin });
    }, [config?.coins, form, selectedCoin]);

    const currentCoin: string = Form.useWatch("coin", form);
    const currentPayMethod: string = Form.useWatch(FORM_KEY_PAYMENTMETHOD, form);
    const amount: string = Form.useWatch(FORM_KEY_AMOUNT, form);

    const currentCoinInfo = useMemo(() => {
        return coins?.find((item) => item.coin === currentCoin);
    }, [coins, currentCoin]);
    const currentPayMethodInfo = useMemo(() => {
        return paymentMethods.find((item) => item.getKey() === currentPayMethod);
    }, [currentPayMethod, paymentMethods]);
    const channel = useMemo(
        () => currentCoinInfo?.channels.find((item) => item.channel === currentPayMethodInfo?.channel),
        [currentCoinInfo?.channels, currentPayMethodInfo?.channel],
    );

    const { previewAmount, previewAmountText, previewPrice, previewAmountPrecision, amountLimit } = usePreviewAmount({ coin: currentCoin, amount, fee: channel?.fee });
    useEffect(() => {
        onPreviewAmount?.({ previewAmount, previewAmountPrecision });
    }, [onPreviewAmount, previewAmount, previewAmountPrecision]);

    const [shortcut, setShortcut] = useState<string>();
    useEffect(() => {
        // 设置默认输入额度
        form.setFieldsValue({ amount: "100" });
        setShortcut("100");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const cacheDefaultPaymentMethod = useDefaultPaymentMethod(form, paymentMethods);
    const shortcuts = useShortcuts();
    const [{ baseAvailable: usdAvailable }] = useBalanceTradeByBaseQuote("USD", "USDT"); // 原始的余额
    const [stripeAchFlag, setStripeAchFlag] = useState<boolean>();

    return (
        <>
            <Form
                form={form}
                className={className}
                labelWrap={true}
                onFinish={(values: any) => {
                    if (usdAvailable <= 0.01) {
                        setStripeAchFlag(true);
                        return;
                    }
                    const fiatAmount = values[FORM_KEY_AMOUNT];
                    const params: BuyCryptoParams = {
                        // TODO 暂时只有ACH
                        ach_account_id: (currentPayMethodInfo!.card as ACH_AccountItem).id,
                        channel_fee: "0",

                        channel: channel!.channel,
                        buy_amount: previewAmount,
                        buy_coin: values["coin"],
                        buy_price: previewPrice,
                        fiat_amount: fiatAmount,
                    };
                    onFinish?.(currentPayMethodInfo!, params);

                    DataReporter.report(EventName.buycrypto_submit, {
                        fiat_amount: params.fiat_amount,
                        channel: params.channel,
                        buy_coin: params.buy_coin,
                    });
                }}
                onValuesChange={(changeValues) => {
                    if (FORM_KEY_PAYMENTMETHOD in changeValues) {
                        const paymentMethodValue = changeValues[FORM_KEY_PAYMENTMETHOD];
                        cacheDefaultPaymentMethod(paymentMethodValue);
                        DataReporter.report(EventName.buycrypto_channel_select, {
                            type: paymentMethods.find((item) => item.getKey() === paymentMethodValue)?.channel,
                        });
                    }

                    if (FORM_KEY_AMOUNT in changeValues) {
                        // 输入amount值的变化，同步快捷选择的状态
                        const amountValue = changeValues[FORM_KEY_AMOUNT];
                        const shortcut = shortcuts.find((item) => new SafeDecimal(amountValue).equals(item));
                        if (!!shortcut) {
                            setShortcut(shortcut);
                        } else {
                            setShortcut(undefined);
                        }
                    }
                }}
            >
                <Spin spinning={!dataLoaded}>
                    <div className="mobile:mb-12px mobile:ml-6px flex justify-between mb-20px items-center pionex-font-poppins">
                        <div className="mobile:text-xl text-accent font-b text-[30px]">{$st("buy_crypto_title")}</div>
                    </div>
                    <div className={csn("bg-[#F7F8FA] rounded-12px px-16px rounded-b-none", "dark:bg-divider")}>
                        <SelectCoinItem coins={coins} />
                        <div className="bg-divider h-1px" />
                        <AmountItem amountLimit={amountLimit} currentPayMethodInfo={currentPayMethodInfo} />
                    </div>
                    {/*<div className="text-accent text-base mb-8px">2. {$st("buy_crypto_input_amount")}</div>*/}
                    <Radio.Group
                        className={csn(cs.shortcuts, "justify-between flex mt-4px")}
                        value={shortcut}
                        size={"small"}
                        onChange={(e) => {
                            const shortcut = e.target.value;
                            form.setFieldsValue({ amount: shortcut.toString() });
                            setShortcut(shortcut);
                        }}
                        defaultValue="100"
                        bgStyle="separate-border"
                    >
                        {shortcuts.map((item) => (
                            <Radio.Button
                                key={item}
                                value={item}
                                onClick={() => {
                                    DataReporter.report(EventName.quickusd_click, { fiat_amount: item, from: "web_click_from" });
                                }}
                            >{`$${NumberUtils.numberWithCommas(item)}`}</Radio.Button>
                        ))}
                    </Radio.Group>
                    <div className="mt-8px mb-16px">
                        <div className="flex justify-between text-accent-sub">
                            {$st("buy_crypto_estimated")}
                            <span className="text-accent font-b">{previewAmountText}</span>
                        </div>

                        {/*{!!moreCoinbase && (*/}
                        {/*    <div*/}
                        {/*        className="hidden mobile:block mt-6px underline text-primary"*/}
                        {/*        onClick={() => {*/}
                        {/*            // 移动端滚动出现比价内容*/}
                        {/*            document.documentElement.scroll({*/}
                        {/*                top: 600,*/}
                        {/*                behavior: "smooth",*/}
                        {/*            });*/}
                        {/*        }}*/}
                        {/*    >*/}
                        {/*        {$st("buy_crypto_more_coinbase", { per: moreCoinbase })}*/}
                        {/*    </div>*/}
                        {/*)}*/}
                    </div>
                    {(isTablet && !userId) || (
                        <>
                            <PayMethodItem form={form} paymentMethods={paymentMethods} refresh={refresh} />
                            <div className="mb-20px mt-6px">{currentPayMethodInfo?.getAmountLimitText() ?? "--"}</div>
                            <KycLoginClick>
                                <Button htmlType="submit" className={csn("w-full", cs.submitBtn)} size="large" type="primary">
                                    {$st("buy_crypto_buy")}
                                </Button>
                            </KycLoginClick>

                            <KycLoginClick>
                                <TReportLink to={BUY_CRYPTO_ROUTE.HISTORY} className="mobile:mb-8px text-accent flex items-center justify-center mt-20px">
                                    <IconHistory24px size={20} className="mr-6px" /> {$st("buy_crypto_history")}
                                </TReportLink>
                            </KycLoginClick>
                        </>
                    )}{" "}
                </Spin>
            </Form>
            {stripeAchFlag !== undefined && (
                <StripeACH
                    modalVisible={stripeAchFlag}
                    onClose={() => {
                        setStripeAchFlag(false);
                    }}
                    amount={amount}
                />
            )}
        </>
    );
};

export default BuyCryptoForm;
